import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SupplierPricingHeader } from './SupplierPricingHeader';
import { SupplierPricingTable } from './SupplierPricingTable';
import { CymbioSpinner } from '../../common/Spinner';
import Pagination, { RowsPerPage, PaginationWrapper } from '../../common/Pagination';
import PageLayout from '../../common/PageLayout';
import { PricingFilters } from '../../filters/PricingFilters';
import { pricingRowsPerPage } from '../../../constants/pricings';
import {
  exportSupplierPricing,
  getSupplierPricing,
  setPricingPage,
  setPricingRowsPerPage,
  uploadSupplierRetailerPricing,
} from '../../../store/actions/pricing';

export function SupplierPricingPage() {
  const dispatch = useDispatch();

  const {
    isLoading,
    filters,
    totalCount,
    table: { rowsPerPage, page },
  } = useSelector((state) => state.pricings);
  const supplierRetailers = useSelector((state) => state.role.supplier.supplier_retailers ?? []);
  const supplierChannels = useMemo(
    () =>
      supplierRetailers
        .map(({ retailer }) => ({ id: retailer.id, name: retailer.name }))
        .sort((a, b) => (a.name > b.name ? 1 : -1)),
    [supplierRetailers],
  );

  const currencies = useMemo(() => {
    const currencies = new Set();
    supplierRetailers.forEach((supplierRetailer) => {
      supplierRetailer.retailer.currencies.forEach((currency) => currencies.add(currency.currency));
    });
    return Array.from(currencies);
  }, [supplierRetailers]);

  const fetchPricing = useCallback(() => {
    dispatch(getSupplierPricing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.currencies.length && filters.channels.length) {
      fetchPricing();
    }
  }, [fetchPricing, filters]);

  const setRowsPerPage = (newRowsPerPage) => {
    dispatch(setPricingRowsPerPage(newRowsPerPage));
    fetchPricing();
  };

  const setPage = (newPage) => {
    dispatch(setPricingPage(newPage));
    fetchPricing();
  };

  return (
    <PageLayout>
      <div className="supplier-pricing-page">
        <SupplierPricingHeader />
        <PricingFilters
          currencies={currencies}
          supplierChannels={supplierChannels}
          supplierRetailers={supplierRetailers}
          onExport={exportSupplierPricing}
          onUpload={uploadSupplierRetailerPricing}
        />

        {isLoading ? (
          <CymbioSpinner />
        ) : (
          <>
            <SupplierPricingTable refetch={fetchPricing} />

            <PaginationWrapper>
              <RowsPerPage options={pricingRowsPerPage} current={rowsPerPage} onChange={setRowsPerPage} />
              <Pagination
                pageCount={Math.ceil(totalCount / rowsPerPage)}
                handlePageClick={({ selected }) => setPage(selected)}
                currentPage={page}
              />
            </PaginationWrapper>
          </>
        )}
      </div>
    </PageLayout>
  );
}
